import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-webapi/commerce-webapi/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "createpayflowprotoken-mutation"
    }}>{`createPayflowProToken mutation`}</h1>
    <p>{`The `}<inlineCode parentName="p">{`createPayflowProToken`}</inlineCode>{` mutation initiates a Payflow Pro transaction and receives a token. The payload must contain the redirect URLs to use when the transaction is successful, when the customer cancels the transaction, or when an error occurs.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`PayflowProUrlInput`}</inlineCode>{` object contains a set of relative URLs that PayPal will use in response to various actions during the authorization process. The application prepends the base URL to this value to create a full URL. For example, if the full URL is `}<inlineCode parentName="p">{`https://www.example.com/path/to/page.html`}</inlineCode>{`, the relative URL is `}<inlineCode parentName="p">{`path/to/page.html`}</inlineCode>{`.`}</p>
    <p>{`Use this input for Payflow Pro and Payment Pro payment methods.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "../../../payment-methods/payflow-pro.md"
      }}>{`PayPal Payflow Pro payment method`}</a>{` for detailed information about the workflow of PayPal Payflow Pro transactions.`}</p>
    <h2 {...{
      "id": "syntax"
    }}>{`Syntax`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`mutation {
  createPayflowProToken(
    input: PayflowProTokenInput
  ) {
    CreatePayflowProTokenOutput
  }
}
`}</code></pre>
    <h2 {...{
      "id": "reference"
    }}>{`Reference`}</h2>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/commerce/webapi/graphql-api/index.html#mutation-createPayflowProToken"
      }}><inlineCode parentName="a">{`createPayflowProToken`}</inlineCode></a>{` reference provides detailed information about the types and fields defined in this mutation.`}</p>
    <h2 {...{
      "id": "example-usage"
    }}>{`Example usage`}</h2>
    <p>{`The following example requests a token in a Payflow Pro transaction.`}</p>
    <p><strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`mutation {
  createPayflowProToken(
    input: {
      cart_id: "Po1WkfK7d3vZE0qga610NwJIbxgqllpt"
      urls: {
        return_url: "paypal/action/return.html"
        cancel_url: "paypal/action/cancel.html"
        error_url: "paypal/action/error.html"
      }
    }
  ) {
    response_message
    result
    result_code
    secure_token
    secure_token_id
  }
}
`}</code></pre>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "data": {
    "createPayflowProToken": {
      "response_message": "Approved",
      "result": 0,
      "result_code": 0,
      "secure_token": "5JRGtIDsaJUuEPq0lR5m9ugqG",
      "secure_token_id": "H3roFRhGjKzxCKr5TlA8mooClBpQxgBY"
    }
  }
}
`}</code></pre>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Error`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Could not find a cart with ID "XXX"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The specified `}<inlineCode parentName="td">{`cart_id`}</inlineCode>{` value does not exist in the `}<inlineCode parentName="td">{`quote_id_mask`}</inlineCode>{` table.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Field PayflowProTokenInput.cart_id of required type String! was not provided.`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`You must specify the `}<inlineCode parentName="td">{`cart_id`}</inlineCode>{` attribute.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Field PayflowProTokenInput.urls of required type PayflowProUrlInput! was not provided.`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`You must specify the `}<inlineCode parentName="td">{`urls`}</inlineCode>{` attribute.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Field PayflowProUrlInput.return_url of required type String! was not provided.`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`You must specify the `}<inlineCode parentName="td">{`return_url`}</inlineCode>{` attribute.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Field PayflowProUrlInput.error_url of required type String! was not provided.`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`You must specify the `}<inlineCode parentName="td">{`error_url`}</inlineCode>{` attribute.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Field PayflowProUrlInput.cancel_url of required type String! was not provided.`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`You must specify the `}<inlineCode parentName="td">{`cancel_url`}</inlineCode>{` attribute.`}</td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      